import React, { useEffect, useState } from "react";
import { api, inr, grams } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import {
  Plus,
  Hammer,
  ArrowDown,
  ArrowUp,
  PencilSimple,
  ListBullets,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STAGES = [
  "assigned",
  "casting",
  "polishing",
  "stone_setting",
  "finishing",
  "completed",
];

export default function Manufacturing() {
  const [karigars, setKarigars] = useState([]);
  const [jobCards, setJobCards] = useState([]);
  const [saving, setSaving] = useState(false);
  const [openK, setOpenK] = useState(false);
  const [openIssue, setOpenIssue] = useState(false);
  const [openReceive, setOpenReceive] = useState(false);
  const [openJC, setOpenJC] = useState(false);
  const [openLedger, setOpenLedger] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [kForm, setKForm] = useState({
    name: "",
    mobile: "",
    address: "",
    skill: "",
    opening_balance_grams: 0,
  });
  const [issueForm, setIssueForm] = useState({
    karigar_id: "",
    gross_weight: 0,
    purity_factor: 0.916,
    notes: "",
  });
  const [recvForm, setRecvForm] = useState({
    karigar_id: "",
    issue_id: "",
    finished_weight: 0,
    finished_purity_factor: 0.916,
    labour_charge: 0,
    notes: "",
  });
  const [jcForm, setJcForm] = useState({
    karigar_id: "",
    product_name: "",
    expected_weight: 0,
    issued_weight: 0,
    status: "assigned",
    notes: "",
    expected_finish_date: "",
  });
  const [editingJC, setEditingJC] = useState(null);

  const load = async () => {
    const [k, j] = await Promise.all([
      api.get("/karigars"),
      api.get("/job-cards"),
    ]);
    setKarigars(k.data);
    setJobCards(j.data);
  };
  useEffect(() => {
    load();
  }, []);

  const saveKarigar = async () => {
    if (!kForm.name.trim()) {
      toast.error("Name required");
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      await api.post("/karigars", {
        ...kForm,
        opening_balance_grams: Number(kForm.opening_balance_grams) || 0,
      });
      toast.success("Karigar added");
      setOpenK(false);
      setKForm({
        name: "",
        mobile: "",
        address: "",
        skill: "",
        opening_balance_grams: 0,
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const saveIssue = async () => {
    if (!issueForm.karigar_id || !Number(issueForm.gross_weight)) {
      toast.error("Karigar + weight required");
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      await api.post("/gold-issues", {
        ...issueForm,
        gross_weight: Number(issueForm.gross_weight),
        purity_factor: Number(issueForm.purity_factor),
      });
      toast.success("Gold issued");
      setOpenIssue(false);
      setIssueForm({
        karigar_id: "",
        gross_weight: 0,
        purity_factor: 0.916,
        notes: "",
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const saveReceive = async () => {
    if (!recvForm.karigar_id || !Number(recvForm.finished_weight)) {
      toast.error("Karigar + finished weight required");
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        ...recvForm,
        finished_weight: Number(recvForm.finished_weight),
        finished_purity_factor: Number(recvForm.finished_purity_factor),
        labour_charge: Number(recvForm.labour_charge) || 0,
      };
      if (!payload.issue_id) payload.issue_id = null;
      await api.post("/gold-receives", payload);
      toast.success("Gold received");
      setOpenReceive(false);
      setRecvForm({
        karigar_id: "",
        issue_id: "",
        finished_weight: 0,
        finished_purity_factor: 0.916,
        labour_charge: 0,
        notes: "",
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const saveJC = async () => {
    if (!jcForm.karigar_id || !jcForm.product_name) {
      toast.error("Karigar + product required");
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        ...jcForm,
        expected_weight: Number(jcForm.expected_weight) || 0,
        issued_weight: Number(jcForm.issued_weight) || 0,
      };
      if (!payload.expected_finish_date) payload.expected_finish_date = null;
      if (editingJC) await api.put(`/job-cards/${editingJC.id}`, payload);
      else await api.post("/job-cards", payload);
      toast.success(editingJC ? "Updated" : "Job card created");
      setOpenJC(false);
      setEditingJC(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const openEditJC = (jc) => {
    setEditingJC(jc);
    setJcForm({
      karigar_id: jc.karigar_id || "",
      product_name: jc.product_name || "",
      expected_weight: jc.expected_weight || 0,
      issued_weight: jc.issued_weight || 0,
      status: jc.status || "assigned",
      notes: jc.notes || "",
      expected_finish_date: jc.expected_finish_date || "",
    });
    setOpenJC(true);
  };

  const showLedger = async (k) => {
    const { data } = await api.get(`/karigars/${k.id}/ledger`);
    setLedger(data);
    setOpenLedger(k);
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="manufacturing-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          Production
        </div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-1">
          Manufacturing & Karigar
        </h1>
      </header>

      <Tabs defaultValue="karigars" className="space-y-6">
        <TabsList className="bg-secondary">
          <TabsTrigger value="karigars" data-testid="tab-karigars">
            Karigars
          </TabsTrigger>
          <TabsTrigger value="jobcards" data-testid="tab-jobcards">
            Job Cards
          </TabsTrigger>
        </TabsList>

        <TabsContent value="karigars" className="space-y-4">
          <div className="flex flex-wrap gap-2 justify-end">
            <Button
              data-testid="add-karigar-btn"
              onClick={() => setOpenK(true)}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Plus size={14} className="mr-1" /> Karigar
            </Button>
            <Button
              data-testid="issue-gold-btn"
              onClick={() => setOpenIssue(true)}
              variant="ghost"
              className="text-gold"
            >
              <ArrowUp size={14} className="mr-1" /> Issue gold
            </Button>
            <Button
              data-testid="receive-gold-btn"
              onClick={() => setOpenReceive(true)}
              variant="ghost"
              className="text-gold"
            >
              <ArrowDown size={14} className="mr-1" /> Receive finished
            </Button>
          </div>
          <div className="rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/40 hover:bg-secondary/40">
                  <TableHead>Karigar</TableHead>
                  <TableHead>Skill</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead className="text-right">Opening (g)</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {karigars.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center py-10 text-muted-foreground"
                    >
                      No karigars yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  karigars.map((k) => (
                    <TableRow key={k.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Hammer
                            className="text-gold"
                            size={14}
                            weight="duotone"
                          />{" "}
                          {k.name}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {k.skill || "—"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {k.mobile || "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {(k.opening_balance_grams || 0).toFixed(3)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`karigar-ledger-${k.id}`}
                          onClick={() => showLedger(k)}
                        >
                          <ListBullets size={14} className="mr-1" /> Ledger
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="jobcards" className="space-y-4">
          <div className="flex justify-end">
            <Button
              data-testid="add-jobcard-btn"
              onClick={() => {
                setEditingJC(null);
                setJcForm({
                  karigar_id: "",
                  product_name: "",
                  expected_weight: 0,
                  issued_weight: 0,
                  status: "assigned",
                  notes: "",
                  expected_finish_date: "",
                });
                setOpenJC(true);
              }}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Plus size={14} className="mr-1" /> New job card
            </Button>
          </div>
          <div className="rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/40 hover:bg-secondary/40">
                  <TableHead>Job No</TableHead>
                  <TableHead>Karigar</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead className="text-right">Exp. Wt</TableHead>
                  <TableHead className="text-right">Issued</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobCards.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center py-10 text-muted-foreground"
                    >
                      No job cards yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  jobCards.map((jc) => (
                    <TableRow key={jc.id}>
                      <TableCell className="font-mono text-xs">
                        {jc.job_no}
                      </TableCell>
                      <TableCell>{jc.karigar_name || "—"}</TableCell>
                      <TableCell>{jc.product_name}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {(jc.expected_weight || 0).toFixed(3)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {(jc.issued_weight || 0).toFixed(3)}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`text-xs uppercase tracking-wider ${jc.status === "completed" ? "text-green-400" : jc.status === "finishing" ? "text-gold" : "text-muted-foreground"}`}
                        >
                          {(jc.status || "").replace("_", " ")}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <button
                          onClick={() => openEditJC(jc)}
                          className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
                        >
                          <PencilSimple size={14} />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add karigar */}
      <Dialog open={openK} onOpenChange={setOpenK}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              New Karigar
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <F
              label="Name *"
              value={kForm.name}
              onChange={(v) => setKForm({ ...kForm, name: v })}
              testid="karigar-name"
            />
            <F
              label="Mobile"
              value={kForm.mobile}
              onChange={(v) => setKForm({ ...kForm, mobile: v })}
            />
            <F
              label="Skill"
              value={kForm.skill}
              onChange={(v) => setKForm({ ...kForm, skill: v })}
            />
            <F
              label="Address"
              value={kForm.address}
              onChange={(v) => setKForm({ ...kForm, address: v })}
            />
            <F
              label="Opening balance (g pure)"
              type="number"
              value={kForm.opening_balance_grams}
              onChange={(v) => setKForm({ ...kForm, opening_balance_grams: v })}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenK(false)}>
              Cancel
            </Button>
            <Button
              data-testid="save-karigar-btn"
              onClick={saveKarigar}
              loading={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issue gold */}
      <Dialog open={openIssue} onOpenChange={setOpenIssue}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              Issue Gold to Karigar
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <SelectField
              label="Karigar *"
              value={issueForm.karigar_id}
              onChange={(v) => setIssueForm({ ...issueForm, karigar_id: v })}
              options={karigars}
              testid="issue-karigar"
            />
            <F
              label="Gross weight (g)"
              type="number"
              value={issueForm.gross_weight}
              onChange={(v) => setIssueForm({ ...issueForm, gross_weight: v })}
              testid="issue-weight"
            />
            <F
              label="Purity factor (0-1)"
              type="number"
              value={issueForm.purity_factor}
              onChange={(v) => setIssueForm({ ...issueForm, purity_factor: v })}
            />
            <F
              label="Notes"
              value={issueForm.notes}
              onChange={(v) => setIssueForm({ ...issueForm, notes: v })}
            />
            <div className="text-xs text-muted-foreground">
              Pure gold issued:{" "}
              <span className="tabular-nums text-gold">
                {(
                  Number(issueForm.gross_weight) *
                    Number(issueForm.purity_factor) || 0
                ).toFixed(3)}{" "}
                g
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenIssue(false)}>
              Cancel
            </Button>
            <Button
              data-testid="save-issue-btn"
              onClick={saveIssue}
              loading={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Issue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receive finished */}
      <Dialog open={openReceive} onOpenChange={setOpenReceive}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              Receive Finished Work
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <SelectField
              label="Karigar *"
              value={recvForm.karigar_id}
              onChange={(v) => setRecvForm({ ...recvForm, karigar_id: v })}
              options={karigars}
              testid="receive-karigar"
            />
            <F
              label="Finished weight (g)"
              type="number"
              value={recvForm.finished_weight}
              onChange={(v) => setRecvForm({ ...recvForm, finished_weight: v })}
              testid="receive-weight"
            />
            <F
              label="Finished purity factor"
              type="number"
              value={recvForm.finished_purity_factor}
              onChange={(v) =>
                setRecvForm({ ...recvForm, finished_purity_factor: v })
              }
            />
            <F
              label="Labour charge (₹)"
              type="number"
              value={recvForm.labour_charge}
              onChange={(v) => setRecvForm({ ...recvForm, labour_charge: v })}
            />
            <F
              label="Issue reference id (optional)"
              value={recvForm.issue_id}
              onChange={(v) => setRecvForm({ ...recvForm, issue_id: v })}
            />
            <F
              label="Notes"
              value={recvForm.notes}
              onChange={(v) => setRecvForm({ ...recvForm, notes: v })}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenReceive(false)}>
              Cancel
            </Button>
            <Button
              data-testid="save-receive-btn"
              onClick={saveReceive}
              loading={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Receive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Job card */}
      <Dialog open={openJC} onOpenChange={setOpenJC}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editingJC ? "Edit" : "New"} Job Card
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <SelectField
              label="Karigar *"
              value={jcForm.karigar_id}
              onChange={(v) => setJcForm({ ...jcForm, karigar_id: v })}
              options={karigars}
              testid="jc-karigar"
            />
            <F
              label="Product name *"
              value={jcForm.product_name}
              onChange={(v) => setJcForm({ ...jcForm, product_name: v })}
              testid="jc-product"
            />
            <F
              label="Expected weight (g)"
              type="number"
              value={jcForm.expected_weight}
              onChange={(v) => setJcForm({ ...jcForm, expected_weight: v })}
            />
            <F
              label="Issued weight (g)"
              type="number"
              value={jcForm.issued_weight}
              onChange={(v) => setJcForm({ ...jcForm, issued_weight: v })}
            />
            <F
              label="Expected finish date"
              type="date"
              value={jcForm.expected_finish_date}
              onChange={(v) =>
                setJcForm({ ...jcForm, expected_finish_date: v })
              }
            />
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Stage
              </Label>
              <Select
                value={jcForm.status}
                onValueChange={(v) => setJcForm({ ...jcForm, status: v })}
              >
                <SelectTrigger className="mt-1.5 bg-background border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => (
                    <SelectItem key={s} value={s} className="capitalize">
                      {s.replace("_", " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <F
              label="Notes"
              value={jcForm.notes}
              onChange={(v) => setJcForm({ ...jcForm, notes: v })}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenJC(false)}>
              Cancel
            </Button>
            <Button
              data-testid="save-jobcard-btn"
              onClick={saveJC}
              loading={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Karigar ledger */}
      <Dialog
        open={!!openLedger}
        onOpenChange={(v) => !v && setOpenLedger(null)}
      >
        <DialogContent className="bg-card border-border max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              Karigar Ledger — {openLedger?.name}
            </DialogTitle>
          </DialogHeader>
          {ledger && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KV
                  label="Issued (pure)"
                  value={`${ledger.total_issued_pure} g`}
                />
                <KV
                  label="Received (pure)"
                  value={`${ledger.total_received_pure} g`}
                />
                <KV label="Wastage" value={`${ledger.total_wastage} g`} />
                <KV
                  label="Outstanding"
                  value={`${ledger.outstanding_pure} g`}
                  accent
                />
              </div>
              <div className="rounded-lg border border-border">
                <div className="px-3 py-2 bg-secondary/40 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Issues
                </div>
                <Table>
                  <TableBody>
                    {ledger.issues.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          className="text-center py-4 text-muted-foreground text-sm"
                        >
                          No issues
                        </TableCell>
                      </TableRow>
                    ) : (
                      ledger.issues.map((i) => (
                        <TableRow key={i.id}>
                          <TableCell className="text-sm">{i.date}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {i.gross_weight}g @ {i.purity_factor}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-gold">
                            {i.pure_weight}g pure
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              <div className="rounded-lg border border-border">
                <div className="px-3 py-2 bg-secondary/40 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Receives
                </div>
                <Table>
                  <TableBody>
                    {ledger.receives.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          className="text-center py-4 text-muted-foreground text-sm"
                        >
                          No receives
                        </TableCell>
                      </TableRow>
                    ) : (
                      ledger.receives.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell className="text-sm">{r.date}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {r.finished_weight}g
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-gold">
                            {r.pure_finished}g pure
                          </TableCell>
                          <TableCell className="text-right text-xs text-muted-foreground">
                            Waste {r.wastage_grams}g · ₹{r.labour_charge}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
      {label}
    </Label>
    <Input
      data-testid={testid}
      type={type}
      className="mt-1.5 bg-background border-border"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

const SelectField = ({ label, value, onChange, options, testid }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
      {label}
    </Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        data-testid={testid}
        className="mt-1.5 bg-background border-border"
      >
        <SelectValue placeholder="Choose" />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.id} value={o.id}>
            {o.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const KV = ({ label, value, accent }) => (
  <div className="rounded-lg border border-border p-3">
    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
      {label}
    </div>
    <div
      className={`font-display text-lg mt-1 tabular-nums ${accent ? "text-gold" : ""}`}
    >
      {value}
    </div>
  </div>
);
